import type {
  SessionsResponse,
  SessionDetailResponse,
  SetSessionHiddenResponse,
  CreateJobResponse,
  CancelJobResponse,
  AnswerQuestionResponse,
  Filters,
  UserPreferencesResponse,
  UpdateUserPreferencesInput,
  ModelsResponse,
  SlackChannelsResponse,
  BroadcastResponse,
  UploadResponse,
  TranscriptionResponse,
  SuggestionsResponse,
  AutomationsResponse,
  AutomationDetailResponse,
  AutomationRunsResponse,
  CreateAutomationResponse,
  UpdateAutomationResponse,
  TriggerAutomationResponse,
  CancelAutomationRunResponse,
  WatchesResponse,
  WatchDetailResponse,
  CancelWatchResponse,
  AutomationSchedule,
  AutomationTarget,
  UsageStatsResponse,
  DocsListResponse,
  DocResponse,
} from "./types";

const API_BASE = "/v1";

async function fetchJson<T>(url: string, options?: RequestInit): Promise<T> {
  const headers = new Headers(options?.headers || {});
  const isFormData = options?.body instanceof FormData;
  if (!isFormData && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }

  const response = await fetch(url, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({}));
    throw new Error(error.error || `HTTP ${response.status}`);
  }

  return response.json();
}

export async function fetchSessions(filters: Filters): Promise<SessionsResponse> {
  const params = new URLSearchParams();
  if (filters.status) params.set("status", filters.status);
  if (filters.source) params.set("source", filters.source);
  if (filters.search) params.set("search", filters.search);
  if (filters.mine) params.set("mine", "true");
  if (filters.includeAutomated) params.set("includeAutomated", "true");
  if (filters.limit) params.set("limit", String(filters.limit));

  const url = params.toString()
    ? `${API_BASE}/sessions?${params.toString()}`
    : `${API_BASE}/sessions`;

  return fetchJson<SessionsResponse>(url);
}

export async function fetchSession(threadKey: string): Promise<SessionDetailResponse> {
  const encodedKey = encodeURIComponent(threadKey);
  return fetchJson<SessionDetailResponse>(`${API_BASE}/sessions/${encodedKey}`);
}

export async function setSessionHidden(
  threadKey: string,
  hidden: boolean,
): Promise<SetSessionHiddenResponse> {
  const encodedKey = encodeURIComponent(threadKey);
  return fetchJson<SetSessionHiddenResponse>(`${API_BASE}/sessions/${encodedKey}/hidden`, {
    method: "PATCH",
    body: JSON.stringify({ hidden }),
  });
}

export async function fetchUserPreferences(): Promise<UserPreferencesResponse> {
  return fetchJson<UserPreferencesResponse>(`${API_BASE}/me/preferences`);
}

export async function updateUserPreferences(
  data: UpdateUserPreferencesInput,
): Promise<UserPreferencesResponse> {
  return fetchJson<UserPreferencesResponse>(`${API_BASE}/me/preferences`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function fetchModels(): Promise<ModelsResponse> {
  return fetchJson<ModelsResponse>(`${API_BASE}/models`);
}

export async function fetchUsageStats(days = 14, mine = true): Promise<UsageStatsResponse> {
  const params = new URLSearchParams();
  params.set("days", String(days));
  if (mine) {
    params.set("mine", "true");
  }
  return fetchJson<UsageStatsResponse>(`${API_BASE}/usage/stats?${params.toString()}`);
}

export async function createJob(
  message: string,
  threadKey?: string,
  mode?: "plan" | "build",
  modelId?: string,
  attachmentIds?: string[],
): Promise<CreateJobResponse> {
  return fetchJson<CreateJobResponse>(`${API_BASE}/jobs`, {
    method: "POST",
    body: JSON.stringify({ message, threadKey, mode, modelId, attachmentIds }),
  });
}

export async function uploadAttachments(files: File[]): Promise<UploadResponse> {
  const formData = new FormData();
  for (const file of files) {
    formData.append("files", file);
  }

  return fetchJson<UploadResponse>(`${API_BASE}/uploads`, {
    method: "POST",
    body: formData,
  });
}

export async function transcribeAudio(
  audioFile: File,
  signal?: AbortSignal,
): Promise<TranscriptionResponse> {
  const formData = new FormData();
  formData.append("audio", audioFile);

  return fetchJson<TranscriptionResponse>(`${API_BASE}/transcriptions/transcribe`, {
    method: "POST",
    body: formData,
    signal,
  });
}

export async function cleanupTranscription(
  text: string,
  signal?: AbortSignal,
): Promise<TranscriptionResponse> {
  return fetchJson<TranscriptionResponse>(`${API_BASE}/transcriptions/cleanup`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ text }),
    signal,
  });
}

export async function cancelJob(jobId: string): Promise<CancelJobResponse> {
  return fetchJson<CancelJobResponse>(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/cancel`, {
    method: "POST",
  });
}

export async function answerQuestion(
  jobId: string,
  answers: string[][],
): Promise<AnswerQuestionResponse> {
  return fetchJson<AnswerQuestionResponse>(`${API_BASE}/jobs/${encodeURIComponent(jobId)}/answer`, {
    method: "POST",
    body: JSON.stringify({ answers }),
  });
}

export async function fetchSlackChannels(): Promise<SlackChannelsResponse> {
  return fetchJson<SlackChannelsResponse>(`${API_BASE}/slack/channels`);
}

export async function broadcastToSlack(
  channel: string,
  message: string,
): Promise<BroadcastResponse> {
  return fetchJson<BroadcastResponse>(`${API_BASE}/slack/broadcast`, {
    method: "POST",
    body: JSON.stringify({ channel, message }),
  });
}

export async function fetchSuggestions(limit = 4): Promise<SuggestionsResponse> {
  const params = new URLSearchParams();
  if (limit) params.set("limit", String(limit));
  return fetchJson<SuggestionsResponse>(`${API_BASE}/suggestions?${params.toString()}`);
}

// Automation API functions
export async function fetchAutomations(): Promise<AutomationsResponse> {
  return fetchJson<AutomationsResponse>(`${API_BASE}/automations`);
}

export async function fetchWatches(
  scope: "active" | "all",
  ownerScope: "personal" | "all" = "personal",
): Promise<WatchesResponse> {
  const params = new URLSearchParams({ scope, owner: ownerScope });
  return fetchJson<WatchesResponse>(`${API_BASE}/watches?${params.toString()}`);
}

export async function fetchWatch(id: string): Promise<WatchDetailResponse> {
  return fetchJson<WatchDetailResponse>(`${API_BASE}/watches/${encodeURIComponent(id)}`);
}

export async function cancelWatch(id: string): Promise<CancelWatchResponse> {
  return fetchJson<CancelWatchResponse>(`${API_BASE}/watches/${encodeURIComponent(id)}/cancel`, {
    method: "POST",
  });
}

export async function fetchAutomation(id: string): Promise<AutomationDetailResponse> {
  return fetchJson<AutomationDetailResponse>(`${API_BASE}/automations/${encodeURIComponent(id)}`);
}

export async function fetchAutomationRuns(id: string): Promise<AutomationRunsResponse> {
  return fetchJson<AutomationRunsResponse>(
    `${API_BASE}/automations/${encodeURIComponent(id)}/runs`,
  );
}

export async function createAutomation(data: {
  name: string;
  prompt: string;
  schedule: AutomationSchedule;
  target?: AutomationTarget;
  modelId?: string | null;
}): Promise<CreateAutomationResponse> {
  return fetchJson<CreateAutomationResponse>(`${API_BASE}/automations`, {
    method: "POST",
    body: JSON.stringify(data),
  });
}

export async function updateAutomation(
  id: string,
  data: {
    name?: string;
    prompt?: string;
    schedule?: AutomationSchedule;
    target?: AutomationTarget;
    modelId?: string | null;
    status?: "enabled" | "disabled";
  },
): Promise<UpdateAutomationResponse> {
  return fetchJson<UpdateAutomationResponse>(`${API_BASE}/automations/${encodeURIComponent(id)}`, {
    method: "PATCH",
    body: JSON.stringify(data),
  });
}

export async function deleteAutomation(id: string): Promise<{ ok: boolean }> {
  return fetchJson<{ ok: boolean }>(`${API_BASE}/automations/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export async function triggerAutomation(id: string): Promise<TriggerAutomationResponse> {
  return fetchJson<TriggerAutomationResponse>(
    `${API_BASE}/automations/${encodeURIComponent(id)}/trigger`,
    {
      method: "POST",
    },
  );
}

export async function cancelAutomationRun(
  automationId: string,
  runId: string,
): Promise<CancelAutomationRunResponse> {
  return fetchJson<CancelAutomationRunResponse>(
    `${API_BASE}/automations/${encodeURIComponent(automationId)}/runs/${encodeURIComponent(runId)}/cancel`,
    { method: "POST" },
  );
}

export async function pauseAutomation(id: string): Promise<UpdateAutomationResponse> {
  return fetchJson<UpdateAutomationResponse>(
    `${API_BASE}/automations/${encodeURIComponent(id)}/pause`,
    {
      method: "POST",
    },
  );
}

export async function resumeAutomation(id: string): Promise<UpdateAutomationResponse> {
  return fetchJson<UpdateAutomationResponse>(
    `${API_BASE}/automations/${encodeURIComponent(id)}/resume`,
    {
      method: "POST",
    },
  );
}

// Docs API functions
export async function fetchDocsList(): Promise<DocsListResponse> {
  return fetchJson<DocsListResponse>(`${API_BASE}/docs`);
}

export async function fetchDoc(slug: string): Promise<DocResponse> {
  return fetchJson<DocResponse>(`${API_BASE}/docs/${encodeURIComponent(slug)}`);
}

// Admin API functions

export type AdminSecret = {
  key: string;
  category: string | null;
  description: string | null;
  updatedAt: number;
  updatedBy: string;
};

export type AdminSecretDetail = AdminSecret & {
  value: string;
};

export type AdminUser = {
  id: string;
  name: string;
  email: string;
  role: "admin" | "member";
  image: string | null;
  createdAt: string;
};

export async function fetchAdminSecrets(): Promise<{ secrets: AdminSecret[] }> {
  return fetchJson(`${API_BASE}/admin/secrets`);
}

export async function fetchAdminSecret(key: string): Promise<AdminSecretDetail> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`);
}

export async function saveAdminSecret(
  key: string,
  data: { value: string; category?: string; description?: string },
): Promise<{ ok: boolean; key: string }> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`, {
    method: "PUT",
    body: JSON.stringify(data),
  });
}

export async function deleteAdminSecret(key: string): Promise<{ ok: boolean; key: string }> {
  return fetchJson(`${API_BASE}/admin/secrets/${encodeURIComponent(key)}`, {
    method: "DELETE",
  });
}

export async function fetchAdminUsers(): Promise<{ users: AdminUser[] }> {
  return fetchJson(`${API_BASE}/admin/users`);
}

export async function updateAdminUserRole(
  id: string,
  role: "admin" | "member",
): Promise<{ ok: boolean; id: string; role: string }> {
  return fetchJson(`${API_BASE}/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify({ role }),
  });
}

export async function confirmCliAuth(userCode: string): Promise<{ ok: boolean }> {
  return fetchJson("/api/auth/cli/confirm", {
    method: "POST",
    body: JSON.stringify({ userCode }),
  });
}
